/**
 * @file Product-analytics helpers around posthog-js.
 *
 * Nothing outside this folder should import `posthog-js` directly for product
 * analytics — go through `track()` / `useTrack()` so every event gets its
 * `feature_area`, and so the demo-surface guard and typing stay enforceable.
 * (`src/lib/logger.ts` is the one exception: it owns error capture.)
 */
import type { BeforeSendFn, CaptureResult, PostHog } from 'posthog-js';
import { STRIVELABS_USERS } from '@/constants';
import {
  EVENT_FEATURE_AREA,
  type AnalyticsEventName,
  type AnalyticsEventProperties,
} from './events';

/** Deploy environment, registered as a super property on every event. */
export type AnalyticsEnvironment = 'development' | 'staging' | 'production';

/**
 * Route prefixes for demo/dev-only surfaces. Events fired while on these paths
 * are dropped before they leave the browser — demo agents and playgrounds are
 * sales/dev tooling, not product usage, and would skew every adoption metric.
 */
export const DEMO_ROUTE_PREFIXES = [
  '/ui/agent/recki',
  '/ui/agent/outbound-call-ai-agent',
  '/ui/agent/lead-funnel-analyser-demo',
  '/ui/agent/content-repurposing',
  '/ui/charts',
  '/ui/slots',
  '/ui/insights',
  '/ui/block-kit-playground',
  '/ui/pdf-renderer',
] as const;

/** UUIDs, ULID/nanoid-ish tokens, and bare numbers become `:id` in route_pattern. */
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
const OPAQUE_ID_SEGMENT = /^(?=.*\d)[A-Za-z0-9_-]{16,}$/;

export const getEnvironment = (): AnalyticsEnvironment => {
  const env = import.meta.env.VITE_APP_NODE_ENV;
  return env === 'production' || env === 'staging' ? env : 'development';
};

/**
 * Collapses id-bearing path segments so dashboards can group by route instead
 * of drowning in one row per record: `/ui/reports/9f3c…` → `/ui/reports/:id`.
 */
export const normalizeRoute = (pathname: string): string => {
  if (!pathname) return pathname;

  const normalized = pathname
    .split('/')
    .map((segment) =>
      UUID_SEGMENT.test(segment) ||
      NUMERIC_SEGMENT.test(segment) ||
      OPAQUE_ID_SEGMENT.test(segment)
        ? ':id'
        : segment,
    )
    .join('/');

  // Drop a trailing slash so `/ui/reports` and `/ui/reports/` are one row.
  return normalized.length > 1 && normalized.endsWith('/')
    ? normalized.slice(0, -1)
    : normalized;
};

export const isDemoPath = (pathname: string | undefined): boolean =>
  !!pathname && DEMO_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * Agent slug from an `/ui/agent/<slug>` route. Agent workflows are triggered
 * from shared hooks that don't know which agent mounted them, and the route is
 * the one reliable source. Demo agents never reach PostHog — `beforeSend`
 * drops everything fired from a demo route.
 */
export const agentSlugFromPath = (pathname: string): string | undefined =>
  pathname.match(/^\/ui\/agent\/([^/]+)/)?.[1];

/**
 * StriveLabs staff, flagged as a person property so dashboards can exclude us
 * without losing the data (we still want to see internal usage on its own).
 */
export const isInternalUser = (email: string | undefined | null): boolean => {
  if (!email) return false;
  const normalized = email.toLowerCase();
  return (
    STRIVELABS_USERS.includes(normalized) || normalized.endsWith('@strivelabs.ai')
  );
};

const pathnameOf = (properties: CaptureResult['properties']): string | undefined => {
  const pathname = properties?.$pathname;
  if (typeof pathname === 'string') return pathname;

  const currentUrl = properties?.$current_url;
  if (typeof currentUrl !== 'string') return undefined;
  try {
    return new URL(currentUrl).pathname;
  } catch {
    return undefined;
  }
};

/**
 * Runs on every outbound event: drops anything fired from a demo surface and
 * stamps `route_pattern` so URL-based insights aggregate cleanly.
 */
export const beforeSend: BeforeSendFn = (event) => {
  if (!event) return event;

  const pathname = pathnameOf(event.properties);
  if (isDemoPath(pathname)) return null;

  if (pathname) {
    event.properties.route_pattern = normalizeRoute(pathname);
  }
  return event;
};

/**
 * Captures a product event. Injects `feature_area` from the taxonomy so call
 * sites can't forget it, and no-ops when PostHog is unavailable (no key in
 * local dev, or the provider hasn't mounted yet).
 *
 * The taxonomy value is applied first so a payload that carries its own
 * `feature_area` wins. Only the cross-surface list events do that: they fire
 * from nine areas, so no static mapping can describe them.
 */
export const track = <E extends AnalyticsEventName>(
  posthog: PostHog | undefined | null,
  event: E,
  properties: AnalyticsEventProperties[E],
): void => {
  posthog?.capture(event, {
    feature_area: EVENT_FEATURE_AREA[event],
    ...properties,
  });
};

/**
 * Sets a person property. For settings that are *state* rather than events:
 * a toggle event tells you someone changed it, this tells you where it landed,
 * so "how many accounts have Slack on" is answerable without replaying history.
 */
export const setPersonProperty = (
  posthog: PostHog | undefined | null,
  properties: Record<string, string | number | boolean>,
): void => {
  posthog?.setPersonProperties(properties);
};

/** Drops keys whose value is undefined, so `$set` never blanks a known value. */
const compact = (properties: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(properties).filter(([, v]) => v !== undefined));

/** Minimal shape we need from Clerk's `UserResource`. */
export interface AnalyticsUser {
  id: string;
  fullName?: string | null;
  username?: string | null;
  createdAt?: Date | null;
  primaryEmailAddress?: { emailAddress?: string | null } | null;
}

/**
 * Identifies the person by Clerk user id — stable, unlike the email we keyed
 * on before — and sets a curated scalar profile. Raw Clerk resources are
 * deliberately not forwarded: they serialise into deeply nested person
 * properties and carry PII (phone numbers, membership lists) we don't need.
 */
export const identifyUser = (
  posthog: PostHog | undefined | null,
  user: AnalyticsUser,
): void => {
  if (!posthog || !user?.id) return;

  const email = user.primaryEmailAddress?.emailAddress ?? undefined;

  posthog.identify(
    user.id,
    // Undefined values are stripped rather than sent. `email` is what PostHog
    // labels sessions and recordings with, so sending it as undefined during a
    // render where Clerk hasn't resolved it would blank out a good value and
    // drop the person back to showing their raw id.
    compact({
      email,
      name: user.fullName ?? undefined,
      clerk_username: user.username ?? undefined,
      is_internal: isInternalUser(email),
    }),
    // Acquisition properties: first value wins, never overwritten on re-login.
    compact({ signed_up_at: user.createdAt?.toISOString() }),
  );
};

/**
 * Associates the person with their organization group and registers the
 * account envelope as super properties, so every subsequent event — custom,
 * autocapture, and pageview alike — carries tenant context for free.
 */
export const identifyOrganization = (
  posthog: PostHog | undefined | null,
  organization: { id: string; name?: string | null } | null | undefined,
  tenantId?: string,
): void => {
  if (!posthog || !organization?.id) return;

  posthog.group('organization', organization.id, compact({
    name: organization.name ?? undefined,
    tenant_id: tenantId,
  }));

  posthog.register(compact({
    organization_id: organization.id,
    organization_name: organization.name ?? undefined,
    tenant_id: tenantId,
    environment: getEnvironment(),
  }));
};

/** Clears identity + super properties. Call before Clerk sign-out. */
export const resetAnalytics = (posthog: PostHog | undefined | null): void => {
  posthog?.reset();
};
